import { useState, useEffect } from "react";
import { isApiConnected } from "@/lib/apiClient";
import { listLiveCourses, matchLiveCourseByName, realCategoryForSlug, type LiveCourseInfo } from "@/services/courseService";
import { SLUG_TO_COURSE_NAME } from "@/services/batchService";
import { courses as mockCourses, upcomingBatches as mockUpcomingBatches } from "@/mocks/siteData";
import { resolveCourseSlug, courseNameToSlug } from "@/lib/courseSlugMap";
import { oldAppDateToTimestamp } from "@/lib/oldAppDate";
import { isCurrentYearOrLater } from "@/lib/calendarYear";
import { computeAvailability, bookedFromAvailable } from "@/services/seatAvailabilityService";

export interface Course {
  id?: string;
  slug: string;
  name: string;
  category: string;
  difficulty: string;
  duration: string;
  price: number;
  seats: number;
  image: string;
  /** Extra real photos for the gallery, beyond `image` (which stays the
   * card/thumbnail image everywhere else). Optional — most courses still
   * only have the one photo. */
  images?: string[];
  shortDescription: string;
  fullDescription: string;
  itinerary: Array<{ day: number; title: string; description: string }>;
  eligibility: string[];
  equipment: string[];
  reviews: number;
  rating: number;
  location: string;
  address?: string;
  mapLinks?: string[];
  subCourses?: Array<{ name: string; duration: string }>;
  highlightNote?: string;
  highlights?: string[];
}

// matchLiveCourseByName's fuzzy substring match fails for courses whose
// mock name and real backend name diverge (e.g. "Advanced Mountaineering
// Course (AMC)" in siteData.ts vs the real "Advance Mountaineering Course
// (AMC)" — confirmed live, "advance" is not a substring of "advanced"), so
// those courses never pick up live price/seats and stay stuck on the
// mock's static price forever. SLUG_TO_COURSE_NAME (batchService.ts) is the
// confirmed-live-correct name per slug — prefer it over the mock's name
// when matching, same fix already applied to category via
// realCategoryForSlug below.
function liveMatchName(mock: Course): string {
  return SLUG_TO_COURSE_NAME[mock.slug] || mock.name;
}

function mergeCourse(mock: Course, live?: LiveCourseInfo): Course {
  // Real Category Name (e.g. "Mountaineering") replaces the mock's broad
  // land/aqua/aero grouping, looked up by slug (reliable) rather than the
  // live-data name match (fragile — see courseService.realCategoryForSlug's
  // comment) so every course gets a correct category, not just the ~7 that
  // happen to fuzzy-match a live row.
  const category = realCategoryForSlug(mock.slug) || mock.category;
  if (!live) return { ...mock, category };
  return {
    ...mock,
    category,
    id: live.id || mock.id,
    price: live.fees || mock.price,
    // course.seats feeds "Max X per batch" on the course-detail page — a
    // fixed capacity figure, not remaining availability. Using
    // live.availableSeats here was a real bug: that number shrinks as
    // bookings come in, so a nearly-full batch would misleadingly show
    // "Max 2 per batch" instead of the batch's actual total capacity.
    seats: live.capacity || mock.seats,
  };
}

export function useCourses(): { courses: Course[]; loading: boolean; error: string | null } {
  const [courses, setCourses] = useState<Course[]>(mockCourses as unknown as Course[]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fallbackData = mockCourses as unknown as Course[];

    if (!isApiConnected()) {
      setCourses(fallbackData);
      setLoading(false);
      return;
    }

    let cancelled = false;

    listLiveCourses()
      .then((live) => {
        if (cancelled) return;
        setCourses(fallbackData.map((mock) => mergeCourse(mock, matchLiveCourseByName(live, liveMatchName(mock)))));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load courses");
        setCourses(fallbackData);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return { courses, loading, error };
}

export function useCourse(slug: string): { course: Course | null; loading: boolean; error: string | null } {
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolvedSlug = resolveCourseSlug(slug);
    const fallback = mockCourses.find((c) => c.slug === resolvedSlug) as unknown as Course | null;

    if (!slug) {
      setLoading(false);
      return;
    }

    if (!fallback) {
      setError("Course not found");
      setLoading(false);
      return;
    }

    if (!isApiConnected()) {
      setCourse(fallback);
      setLoading(false);
      return;
    }

    let cancelled = false;

    listLiveCourses()
      .then((live) => {
        if (cancelled) return;
        setCourse(mergeCourse(fallback, matchLiveCourseByName(live, liveMatchName(fallback))));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load course");
        setCourse(fallback);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  return { course, loading, error };
}

export type BatchSeatStatus = "available" | "limited" | "fullyBooked";

export interface UpcomingBatch {
  courseSlug: string;
  courseName: string;
  serialNo: string;
  startDate: string;
  endDate: string;
  seatsTotal: number;
  seatsBooked: number;
  seatsAvailable: number;
  seatStatus: BatchSeatStatus;
  price: number;
  status: string;
}

// Seats <= this count (but still > 0) are flagged "limited" rather than
// plain "available" — matches the urgency threshold BatchesSection already
// used client-side (>=80% booked = "Almost Full!"). The underlying
// available/booked/soldOut math itself always comes from
// seatAvailabilityService's computeAvailability — the same single formula
// used by BatchSelection, BatchCard and the admin Batches page — so a given
// batch's seat numbers can never disagree between this section and any
// other page that shows the same batch.
const LIMITED_SEATS_THRESHOLD = 3;

// Land -> Air -> Aqua vertical grouping for "Available Batches", matching
// the same order used on /courses — real category names as returned by
// realCategoryForSlug() (Sport Climbing/White Water Kayaking fold into
// Mountaineering/White Water Rafting there, so they aren't listed
// separately here). Slugs with no mapping (or no realCategoryForSlug entry)
// sort to the end rather than being dropped.
const BATCH_VERTICAL_ORDER = [
  // Land
  "Mountaineering",
  "Mountain Terrain Biking",
  // Air
  "Paragliding",
  "Paramotor",
  // Aqua
  "SCUBA Diving",
  "White Water Rafting",
];
function batchVerticalRank(courseSlug: string): number {
  const category = realCategoryForSlug(courseSlug);
  const idx = category ? BATCH_VERTICAL_ORDER.indexOf(category) : -1;
  return idx === -1 ? BATCH_VERTICAL_ORDER.length : idx;
}

function seatStatusFor(availableSeats: number, soldOut: boolean): BatchSeatStatus {
  if (soldOut) return "fullyBooked";
  if (availableSeats <= LIMITED_SEATS_THRESHOLD) return "limited";
  return "available";
}

// Home page's "Available Batches" section — shows templateID 3 batches
// scheduled in the current Financial Year (Apr 1 - Mar 31), fetched live,
// per explicit instruction. Fully booked batches are filtered out (also per
// explicit instruction — this section only lists batches a visitor can
// actually still book), while batches that have already started but still
// have open seats stay visible. Only falls back to the static
// mocks/siteData.ts list when the API is unreachable/erroring — an empty
// live result (API up, genuinely zero current-FY batches with open seats)
// is reported via `empty`, not silently replaced with mock data from an
// unrelated year.
export function useUpcomingBatches(): { batches: UpcomingBatch[]; loading: boolean; error: string | null; empty: boolean } {
  const [batches, setBatches] = useState<UpcomingBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    if (!isApiConnected()) {
      const mockAvailable = mockUpcomingBatches
        .map((b) => {
          const { availableSeats, soldOut } = computeAvailability(b.seatsTotal, b.seatsBooked);
          return {
            ...b,
            seatsAvailable: availableSeats,
            seatStatus: seatStatusFor(availableSeats, soldOut),
            status: "active",
          };
        })
        .filter((b) => b.seatStatus !== "fullyBooked")
        .sort((a, b) => batchVerticalRank(a.courseSlug) - batchVerticalRank(b.courseSlug));
      setBatches(mockAvailable);
      setEmpty(mockAvailable.length === 0);
      setLoading(false);
      return;
    }

    let cancelled = false;

    listLiveCourses()
      .then((live) => {
        if (cancelled) return;

        // Current calendar year onwards (Jan 1 cutoff, computed off the real clock).
        const currentYearOnwards = live
          .filter((c) => isCurrentYearOrLater(oldAppDateToTimestamp(c.datesFrom)))
          .map((c) => ({ batch: c, slug: courseNameToSlug[c.courseName] }))
          .sort((a, b) => oldAppDateToTimestamp(a.batch.datesFrom) - oldAppDateToTimestamp(b.batch.datesFrom))
          .map(({ batch, slug }): UpcomingBatch => {
            // bookedFromAvailable/computeAvailability: the same seat-math
            // pipeline used everywhere else (BatchSelection, BatchCard,
            // admin Batches) — trusts the batch's own stored "Available
            // Seats" field as ground truth rather than recomputing from
            // Bookings records (confirmed live to match the production
            // site; see seatAvailabilityService.ts).
            const bookedSeats = bookedFromAvailable(batch.capacity, batch.availableSeats);
            const { totalSeats, availableSeats, soldOut } = computeAvailability(batch.capacity, bookedSeats);
            return {
              // Falls back to the batch's own serial number as a routable
              // slug when its course name has no entry in courseNameToSlug
              // — keeps the listing visible (never dropped) even though the
              // "Book Now" link can't resolve to a real course page for it.
              courseSlug: slug || batch.serialNo,
              courseName: batch.courseName,
              serialNo: batch.serialNo,
              startDate: batch.datesFrom,
              endDate: batch.datesTo,
              seatsTotal: totalSeats,
              seatsBooked: bookedSeats,
              seatsAvailable: availableSeats,
              seatStatus: seatStatusFor(availableSeats, soldOut),
              price: batch.fees,
              status: batch.status,
            };
          })
          .filter((b) => b.seatStatus !== "fullyBooked")
          .sort((a, b) => batchVerticalRank(a.courseSlug) - batchVerticalRank(b.courseSlug));

        setBatches(currentYearOnwards);
        setEmpty(currentYearOnwards.length === 0);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load upcoming batches");
        const mockAvailable = mockUpcomingBatches
          .map((b) => {
            const { availableSeats, soldOut } = computeAvailability(b.seatsTotal, b.seatsBooked);
            return {
              ...b,
              seatsAvailable: availableSeats,
              seatStatus: seatStatusFor(availableSeats, soldOut),
              status: "active",
            };
          })
          .filter((b) => b.seatStatus !== "fullyBooked")
          .sort((a, b) => batchVerticalRank(a.courseSlug) - batchVerticalRank(b.courseSlug));
        setBatches(mockAvailable);
        setEmpty(mockAvailable.length === 0);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return { batches, loading, error, empty };
}
